import { useRef, useState } from 'react';
import omit from 'lodash/fp/omit';
import isEmpty from 'lodash/fp/isEmpty';
import classNames from 'classnames';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import { Dialog, TableViewTogglesViewType } from '@rio-cloud/rio-uikit';
import MapEvents from '../../shared/components/Maps';
import { useSelectVehicleStore } from '../../services/selectVehicleSlice';
import { ColumnDetailsMap } from './models/ColumnDetailsMap';
import { ColumnLabel } from './models/ColumnLabel';
import { IGoodPractices } from './models/GoodPractices';
import { useGoodPracticesData } from './hooks/useGoodPracticesData';
import { useTableSettings } from './hooks/useTableSettings';
import { useWindowResize } from './hooks/useWindowResize';
import DashboardTableToolbar from './components/DashboardTableToolbar';
import DashboardTableBody from './components/DashboardTableBody';
import style from './DashboardTableGoodPractices.module.css'

export type DashboardTableProps = {
  viewType: TableViewTogglesViewType;
  hasData: boolean;
};

const columnLabels: ColumnLabel = {
  tripStart: 'Início da Trip',
  tripFinal: 'Final da Trip',
  itOvernight: 'Pernoite com Tanque Cheio',
  itDayHours: 'Jornada Diária',
  itTripHours: 'Duração da Viagem',
  detailLastUpdate: 'Data/Hora',
  detailEventCoordinates: 'Localização',
  detailEvents: 'Ocorrências',
  extraAction: 'Expandir Tabela',
};
const bestPracticesColumnOrder = Object.keys(columnLabels);
const disabledColumns: string[] = [];
const subRows = ['detailLastUpdate', 'detailEventCoordinates', 'detailEvents'];

const DashboardTableGoodPractices = (props: DashboardTableProps) => {
  const { viewType: externalViewType, hasData } = props;
  const { selectedVehicle } = useSelectVehicleStore();

  const {
    goodPracticesList,
    isLoading,
    error,
    searchValue,
    setSearchValue,
    handleViewTypeChange,
    viewType,
    handleToggleRow,
    expandedRows,
  } = useGoodPracticesData({
    externalViewType,
    hasData,
  });

  const {
    showTableSettingsDialog,
    hiddenColumns,
    columnsDetails,
    columnOrder,
    handleToggleTableSettingsDialog,
    handleColumnChange,
    handleColumnDetailsChange,
  } = useTableSettings({
    bestPracticesColumnOrder,
    columnsDetailsDescriptor: {} as ColumnDetailsMap,
    columnLabels,
    disabledColumns,
  });

  const menuRef = useRef(document.getElementsByClassName('AssetTree')[0]);
  const lcmCardRef = useRef(document.getElementsByClassName('StatsWidget-wrapper')[0]);
  const { width, screenWidth } = useWindowResize(menuRef, lcmCardRef);

  const columns = columnOrder.filter((name: string) => !hiddenColumns.includes(name) && !subRows.includes(name));
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = goodPracticesList.map((vehicle: IGoodPractices) => ({ ...withoutHidden(vehicle) })) as IGoodPractices[];

  const searchResult = !searchValue
    ? filteredRows
    : filteredRows.filter((row: Partial<IGoodPractices>) =>
        columns.some((col: string) => {
          const value = row[col as keyof IGoodPractices];
          if (value == null) return false;
          return row[col as keyof IGoodPractices]?.toString().toLowerCase().includes(searchValue.toLowerCase());
        })
      );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;
  const tableClassNames = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-bordered',
    'table-sticky',
    'table-head-filled',
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && 'table-cards table-single-card',
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && 'table-cards table-multi-cards'
  );

  const [showDialog, setShowDialog] = useState(false);
  const [lat, setLat] = useState<number>(0);
  const [lng, setLng] = useState<number>(0);

  const handleShowMap = (latitude: number, longitude: number) => {
    setLat(latitude);
    setLng(longitude);
    setShowDialog(true);
  };

  return (
    <div>
      <DashboardTableToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        viewType={viewType}
        onViewTypeChange={handleViewTypeChange}
        selectedVehicle={selectedVehicle}
        onToggleSettings={handleToggleTableSettingsDialog}
        showTableSettingsDialog={showTableSettingsDialog}
        onHideSettings={handleToggleTableSettingsDialog}
        onColumnChange={handleColumnChange}
        onColumnDetailsChange={handleColumnDetailsChange}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        bestPracticesColumnOrder={bestPracticesColumnOrder}
        disabledColumns={disabledColumns}
        hasData={!isEmpty(goodPracticesList)}
      />

      <div
        style={{
          overflowX: screenWidth < 1734 ? 'auto' : undefined,
          maxWidth: screenWidth < 1734 ? width : undefined,
          height: screenWidth < 1734 ? '40vh' : undefined,
        }}
      >
        <DashboardTableBody
          searchResult={searchResult}
          columns={columns}
          columnLabels={columnLabels}
          columnsDetails={columnsDetails}
          tableClassNames={tableClassNames}
          isLoading={isLoading}
          isViewTypeTable={isViewTypeTable}
          screenWidth={screenWidth}
          width={width}
          expandedRows={expandedRows}
          handleToggleRow={handleToggleRow}
          handleShowMap={handleShowMap}
        />
      </div>

      <Dialog
        show={showDialog}
        body={<MapEvents lat={lat} lng={lng} />}
        title={`Localização do Evento - ${selectedVehicle.name}`}
        onClose={() => setShowDialog(false)}
        showCloseButton
        bsSize='lg'
      />
    </div>
  );
};

export default DashboardTableGoodPractices;